import React, { createContext, useContext, useEffect, useState } from 'react'
import { api } from '@/lib/api'

export interface AuthUserEmpresa {
  id: number
  rut: string
  nombre: string
  razon_social: string | null
  nombre_fantasia: string | null
  email: string | null
  telefono: string | null
  logo_url: string | null
  tiene_mandantes: boolean
  // Entitlements por-empresa (SDD suspender-acceso-app-ia). `EmpresaResource`
  // los expone en webLogin y en GET /me/empresa. El enforcement real vive en
  // la API (middlewares); acá solo se usan para gating VISUAL.
  acceso_app_habilitado: boolean
  acceso_ia_habilitado: boolean
}

export interface AuthUser {
  id: number
  name: string
  email: string
  rut?: string
  iniciales: string
  empresa_id: number
  persona_id?: number
  empresa?: AuthUserEmpresa
  is_super_admin?: boolean
  tours_vistos?: Record<string, boolean>
}

export interface WebSession {
  empresa: AuthUserEmpresa | null
  persona_id?: number
  cargo?: { id: number; nombre: string } | null
  token: string
  token_type: 'Bearer'
  is_super_admin?: boolean
}

interface LoginResponse {
  usuario: AuthUser
  sessions: WebSession[]
  // Legacy key returned by webSignup transitional dual-shape (ADR-4)
  access_token?: string
}

export interface SignupPayload {
  nombre: string
  apellido: string
  email: string
  password: string
  password_confirmation: string
  empresa_rut: string
  empresa_nombre: string
  rut_usuario: string
  // Copia el correo del dueño a los 3 emails de la empresa (contacto,
  // registros, vencimientos). El backend es quien materializa la copia.
  usar_email_usuario?: boolean
  // Aceptación explícita de Términos + Política de Privacidad. Obligatorio:
  // el backend lo valida con `accepted` y rechaza el signup sin él. No lleva
  // versión — la estampa el backend desde config/legal.php.
  acepta_terminos: boolean
}

interface UserContextType {
  user: AuthUser | null
  sessions: WebSession[]
  activeEmpresaId: number | null
  isLoading: boolean
  /**
   * `recordar` = checkbox "Mantener sesión abierta en este equipo". Con `false`
   * la lista de sesiones vive sólo en sessionStorage y muere con la pestaña.
   */
  login: (rut: string, password: string, recordar?: boolean) => Promise<{ sessions: WebSession[] }>
  selectEmpresa: (empresaId: number) => Promise<void>
  switchEmpresa: (empresaId: number) => Promise<void>
  createEmpresa: (empresaRut: string, empresaNombre: string, usarEmailUsuario?: boolean) => Promise<void>
  signup: (payload: SignupPayload) => Promise<void>
  logout: (allDevices?: boolean) => Promise<void>
  refreshUser: () => Promise<void>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

// Modelo híbrido de almacenamiento de sesión (multi-pestaña):
//
// - La LISTA de sesiones (qué empresas puede abrir este navegador, con sus
//   tokens) es compartida y debe sobrevivir al cierre/reapertura del navegador
//   → localStorage. Esto evita re-pedir login en cada pestaña nueva.
// - El token ACTIVO y la empresa ACTIVA son POR PESTAÑA → sessionStorage. Así
//   una pestaña en la empresa A no pisa el token de otra pestaña en la empresa
//   B. El token que cada pestaña ENVÍA (lib/api.ts) sale de su propio
//   sessionStorage, que es lo que garantiza que el dato caiga en la empresa
//   correcta aunque localStorage sea sobrescrito por otro login.
const STORAGE_SESSIONS = 'auth_sessions' // localStorage (compartido, persistente)
const STORAGE_TOKEN = 'auth_token' // sessionStorage (por pestaña)
const STORAGE_ACTIVE_EMPRESA = 'auth_active_empresa_id' // sessionStorage (por pestaña)

// "Mantener sesión abierta en este equipo" (checkbox del login).
//
// Marcado (default) → la lista de sesiones va a localStorage: es el
// comportamiento histórico, y el único que permite abrir una pestaña nueva o
// volver mañana sin re-tipear credenciales.
//
// Desmarcado → la MISMA lista va a sessionStorage. Al cerrar la pestaña se
// borra sola. El precio, que es exactamente lo que la persona pidió al
// desmarcar, es que una pestaña nueva tampoco la ve y hay que volver a entrar.
//
// La PREFERENCIA sí vive siempre en localStorage: es un ajuste, no una
// credencial, y recordarla es la mitad de la utilidad del checkbox.
const STORAGE_PERSISTENTE = 'auth_sessions_persistente' // localStorage ('0' = no persistir)

function sesionesPersistentes(): boolean {
  return localStorage.getItem(STORAGE_PERSISTENTE) !== '0'
}

/** El almacén donde vive HOY la lista de sesiones, según la preferencia. */
function almacenSesiones(): Storage {
  return sesionesPersistentes() ? localStorage : sessionStorage
}

/**
 * Lee la lista de sesiones sin importar dónde quedó guardada.
 *
 * sessionStorage primero: si la pestaña tiene una lista propia (login sin
 * "mantener sesión"), esa manda sobre cualquier resto que haya quedado en
 * localStorage de un login anterior.
 */
function leerSessions(): string | null {
  return sessionStorage.getItem(STORAGE_SESSIONS) ?? localStorage.getItem(STORAGE_SESSIONS)
}

/** Guarda en el almacén vigente y limpia el otro, para no dejar copias viejas. */
function guardarSessions(lista: WebSession[]): void {
  const destino = almacenSesiones()
  const otro = destino === localStorage ? sessionStorage : localStorage
  destino.setItem(STORAGE_SESSIONS, JSON.stringify(lista))
  otro.removeItem(STORAGE_SESSIONS)
}

/** Borra la lista de ambos almacenes. Lo usa el logout y el desalojo por 401. */
function borrarSessions(): void {
  localStorage.removeItem(STORAGE_SESSIONS)
  sessionStorage.removeItem(STORAGE_SESSIONS)
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [sessions, setSessions] = useState<WebSession[]>([])
  const [activeEmpresaId, setActiveEmpresaId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Refetchea GET /me/empresa (fuente canónica de EmpresaResource, D1 del
  // design) y mergea los flags de acceso en user.empresa. Cubre el caso donde
  // el admin de plataforma cambia el flag mid-sesión (fc-admin PATCH
  // admin/empresas/{id}/accesos) — el bootstrap del login por sí solo no lo
  // vería hasta el próximo login. Best-effort: si falla (sin empresa activa,
  // red caída), se conserva el flag ya presente sin romper la sesión — la API
  // sigue siendo la fuente de verdad del enforcement real.
  const refreshEmpresaAccesos = async (): Promise<void> => {
    try {
      const res = await api.get<{
        data: { acceso_app_habilitado: boolean; acceso_ia_habilitado: boolean }
      }>('/me/empresa')
      setUser((prev) =>
        prev?.empresa
          ? {
              ...prev,
              empresa: {
                ...prev.empresa,
                acceso_app_habilitado: res.data.acceso_app_habilitado,
                acceso_ia_habilitado: res.data.acceso_ia_habilitado,
              },
            }
          : prev
      )
    } catch {
      // Sin empresa activa (super-admin) o error de red — no-op.
    }
  }

  // Rehydrate on mount: each tab resolves its OWN active token (sessionStorage)
  // and falls back to the shared session list (localStorage) when it has none.
  useEffect(() => {
    // Empresas que este navegador puede abrir (compartido entre pestañas).
    let parsedSessions: WebSession[] = []
    const storedSessions = leerSessions()
    if (storedSessions) {
      try {
        parsedSessions = JSON.parse(storedSessions)
        setSessions(parsedSessions)
      } catch {
        borrarSessions()
      }
    }

    // Token y empresa activos son por pestaña.
    let token = sessionStorage.getItem(STORAGE_TOKEN)

    // Pestaña nueva (sessionStorage vacío) pero el navegador ya tiene EXACTAMENTE
    // una sesión: auto-reanudar sin pedir login. Con varias sesiones dejamos la
    // pestaña sin autenticar para que Login muestre el selector de empresa.
    if (!token && parsedSessions.length === 1) {
      const single = parsedSessions[0]
      token = single.token
      sessionStorage.setItem(STORAGE_TOKEN, single.token)
      const empresaId = single.empresa?.id ?? null
      if (empresaId !== null) {
        sessionStorage.setItem(STORAGE_ACTIVE_EMPRESA, String(empresaId))
      }
    }

    const storedEmpresaId = sessionStorage.getItem(STORAGE_ACTIVE_EMPRESA)
    if (storedEmpresaId) {
      setActiveEmpresaId(Number(storedEmpresaId))
    }

    if (!token) {
      setIsLoading(false)
      return
    }

    api
      .get<{ data: AuthUser }>('/auth/user')
      .then((res) => {
        setUser(res.data)
        return refreshEmpresaAccesos()
      })
      .catch(() => {
        // Token activo inválido — limpiar SOLO esta pestaña; la lista de
        // sesiones queda para que el usuario pueda re-seleccionar empresa.
        sessionStorage.removeItem(STORAGE_TOKEN)
        sessionStorage.removeItem(STORAGE_ACTIVE_EMPRESA)
        setActiveEmpresaId(null)
      })
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    const handleForceLogout = () => {
      // api.ts ya removió el token de esta pestaña ante el 401. Acá limpiamos el
      // resto del estado de sesión para no dejar el selector de Login con
      // empresas obsoletas (que provocarían un loop de 401 al re-seleccionar).
      sessionStorage.removeItem(STORAGE_ACTIVE_EMPRESA)
      borrarSessions()
      setSessions([])
      setActiveEmpresaId(null)
      setUser(null)
    }
    window.addEventListener('auth:logout', handleForceLogout)
    return () => window.removeEventListener('auth:logout', handleForceLogout)
  }, [])

  const login = async (
    rut: string,
    password: string,
    recordar = true
  ): Promise<{ sessions: WebSession[] }> => {
    // El login web usa RUT como credencial canónica (no email). El email
    // puede diferir entre empresas para el mismo trabajador, así que no
    // sirve para identificarlo unívocamente.
    const res = await api.post<LoginResponse>('/auth/web/login', { rut, password })

    // La preferencia se fija ANTES de guardar: `guardarSessions()` la lee para
    // decidir el almacén.
    localStorage.setItem(STORAGE_PERSISTENTE, recordar ? '1' : '0')

    // Persist all sessions so the user can switch without re-login
    setSessions(res.sessions)
    guardarSessions(res.sessions)

    // Auto-select when only one session is available (N=1 or super-admin)
    if (res.sessions.length === 1) {
      const single = res.sessions[0]
      sessionStorage.setItem(STORAGE_TOKEN, single.token)

      const empresaId = single.empresa?.id ?? null
      setActiveEmpresaId(empresaId)
      if (empresaId !== null) {
        sessionStorage.setItem(STORAGE_ACTIVE_EMPRESA, String(empresaId))
      }

      // El payload `usuario` del login es deliberadamente AGNÓSTICO DE EMPRESA:
      // con N>1 sesiones sería ambiguo, porque el mismo user es una Persona
      // distinta en cada empresa. Por eso `usuario` sólo trae id/name/email/rut/
      // is_super_admin/tours_vistos — NO trae `empresa`, `empresa_id`,
      // `persona_id` ni `iniciales`, que sólo existen atados a un token con
      // empresa.
      //
      // Construir el user desde `res.usuario` dejaba esos cuatro campos en
      // undefined hasta el próximo remount. El daño visible: el gate de
      // onboarding lee `user.persona_id` y con undefined muestra el error duro
      // "No tenemos vinculada tu persona al sistema" a un owner que SÍ tiene su
      // Persona. Por eso hidratamos desde GET /auth/user con el token recién
      // activado — la MISMA fuente canónica que usan selectEmpresa(),
      // createEmpresa() y la rehidratación al montar. Un solo shape de AuthUser,
      // se llegue por donde se llegue.
      const userRes = await api.get<{ data: AuthUser }>('/auth/user')
      setUser(userRes.data)
    }
    // N>1: caller (Login.tsx) will render the empresa selector

    return { sessions: res.sessions }
  }

  const selectEmpresa = async (empresaId: number): Promise<void> => {
    // Find the session corresponding to this empresa
    const session = sessions.find((s) => s.empresa?.id === empresaId || s.is_super_admin)
    if (!session) return

    sessionStorage.setItem(STORAGE_TOKEN, session.token)
    sessionStorage.setItem(STORAGE_ACTIVE_EMPRESA, String(empresaId))
    setActiveEmpresaId(empresaId)

    // Fetch user data with the newly active token
    const res = await api.get<{ data: AuthUser }>('/auth/user')
    setUser(res.data)
    // Refetch canónico de accesos (D1/D2 del design) — cubre cambios hechos
    // por el admin mientras esta pestaña tenía otra empresa activa.
    await refreshEmpresaAccesos()
  }

  // switchEmpresa: same as selectEmpresa for this iteration.
  // El feedback (toast) y el redirect viven en el componente EmpresaSwitcher.
  const switchEmpresa = async (empresaId: number): Promise<void> => {
    return selectEmpresa(empresaId)
  }

  // Crea una empresa nueva atada al usuario autenticado (queda como owner) y la
  // deja activa en ESTA pestaña. El backend (POST /auth/web/empresas) devuelve
  // la sesión nueva (empresa + token empresa-scoped); la agregamos a la lista
  // compartida y activamos su token/empresa por pestaña.
  const createEmpresa = async (
    empresaRut: string,
    empresaNombre: string,
    usarEmailUsuario = false,
  ): Promise<void> => {
    const res = await api.post<{ session: WebSession }>('/auth/web/empresas', {
      empresa_rut: empresaRut,
      empresa_nombre: empresaNombre,
      usar_email_usuario: usarEmailUsuario,
    })

    const newSession = res.session

    // Agregar a la lista compartida (localStorage) para que aparezca en el
    // selector y sobreviva a pestañas nuevas.
    const next = [...sessions, newSession]
    setSessions(next)
    guardarSessions(next)

    // Activar la empresa recién creada en esta pestaña. Seteamos el token
    // directo desde newSession (no vía selectEmpresa) porque el estado
    // `sessions` recién actualizado aún no es visible en este closure.
    const empresaId = newSession.empresa?.id ?? null
    if (empresaId !== null) {
      sessionStorage.setItem(STORAGE_TOKEN, newSession.token)
      sessionStorage.setItem(STORAGE_ACTIVE_EMPRESA, String(empresaId))
      setActiveEmpresaId(empresaId)

      // Cargar el usuario con el token nuevo (ya apunta a la empresa nueva).
      const userRes = await api.get<{ data: AuthUser }>('/auth/user')
      setUser(userRes.data)
    }
  }

  const signup = async (payload: SignupPayload) => {
    const res = await api.post<LoginResponse>('/auth/web/signup', payload)

    // Prefer sessions[0] (new shape); fall back to legacy access_token key
    const token = res.sessions?.[0]?.token ?? res.access_token
    if (!token) return

    sessionStorage.setItem(STORAGE_TOKEN, token)

    if (res.sessions?.length) {
      setSessions(res.sessions)
      guardarSessions(res.sessions)

      const empresaId = res.sessions[0].empresa?.id ?? null
      setActiveEmpresaId(empresaId)
      if (empresaId !== null) {
        sessionStorage.setItem(STORAGE_ACTIVE_EMPRESA, String(empresaId))
      }
    }

    setUser(res.usuario)
  }

  const logout = async (allDevices = false) => {
    try {
      const path = allDevices ? '/auth/logout?all=1' : '/auth/logout'
      await api.post(path)
    } finally {
      // Token + empresa activos: solo esta pestaña (sessionStorage).
      sessionStorage.removeItem(STORAGE_TOKEN)
      sessionStorage.removeItem(STORAGE_ACTIVE_EMPRESA)
      // Lista de sesiones compartida: logout completo del navegador.
      borrarSessions()
      setSessions([])
      setActiveEmpresaId(null)
      setUser(null)
    }
  }

  const refreshUser = async () => {
    const res = await api.get<{ data: AuthUser }>('/auth/user')
    setUser(res.data)
  }

  return (
    <UserContext.Provider
      value={{
        user,
        sessions,
        activeEmpresaId,
        isLoading,
        login,
        selectEmpresa,
        switchEmpresa,
        createEmpresa,
        signup,
        logout,
        refreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
